import { createSignal, type JSX } from 'solid-js';
import { AccountKeysView } from '../components/AccountKeysView';
import { t } from '../i18n';
import { useOrgKeyCapacityCards } from '../lib/key-capacity';
import { notifyKeyCapture } from '../lib/key-capture';
import { useOrgKeyPriority } from '../lib/key-priority';
import { keyVerifyNotice } from '../lib/key-verify';
import { createKeyedMutation } from '../lib/keyed-mutation';
import { createKeyRotate } from '../lib/keyRotate';
import {
  type AddKeyInput,
  addKey,
  type KeyRow,
  type ProbeModelsInput,
  probeProviderModels,
  revokeKey,
  rotateKey,
  useKeys,
  verifyKey,
} from '../lib/keys';
import { createLogSinkEditor } from '../lib/log-sink-editor';
import { createMutation } from '../lib/mutation';
import { useOrgKeyGate } from '../lib/org-key-gate';
import { canAdminWrite, roleSettled } from '../lib/roles';
import { SESSION_TOKEN, useMe } from '../lib/session';
import { createToast } from '../lib/toast';

// Org BYOK keys CONTAINER. Reads `GET /v1/billing/keys`; add + verify + revoke
// live behind overlays/actions; the JSX presenter is `AccountKeysView`. `token`
// is injectable for tests (mirrors `Onboarding`); the router mounts this with no
// props → dev session. After a mutation we refetch and render server truth.
// Revoke failures surface as an error toast while the confirm stays open — a
// destructive action must never silently no-op.

export type { KeyRow } from '../lib/keys';

export function AccountKeys(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const query = useKeys(token);
  // Personal keys but no org key = zero usable keys (`validOrgKey` needs
  // `user_id IS NULL`). Both key pages say so; the predicate is shared.
  const personalKeysOnly = useOrgKeyGate(token);
  let heading: HTMLHeadingElement | undefined;

  const [addOpen, setAddOpen] = createSignal(false);
  const [revoking, setRevoking] = createSignal<KeyRow | null>(null);

  const { toast, notify, dismiss } = createToast();

  // Rotate (#971) — `lib/keyRotate.ts` holds the 200/422 contract.
  const rotate = createKeyRotate({
    rotate: (id, body) => rotateKey(id, body, token),
    refetch: () => query.refetch(),
    notify,
  });

  // Re-rank (plan 102 slice 05b): `write:billing`, shut until THIS org's `/v1/me`
  // answered (`roleSettled`, never a prior org's placeholder); the 403 arbitrates.
  const me = useMe(token);
  const priority = useOrgKeyPriority(token, notify);
  const canRank = (): boolean => roleSettled(me) && canAdminWrite(me.data?.role);
  const capacity = useOrgKeyCapacityCards(token, notify, canRank);

  // The resync runs on BOTH outcomes and inside the pending window: a refused add
  // may still have sealed a row, and the server's list is the only thing that
  // knows — so the dialog stays busy until the card list reflects it.
  const add = createMutation({
    call: (input: AddKeyInput) => addKey(input, token),
    fallback: t('account_keys.add_error'),
    onSuccess: (answer) => {
      setAddOpen(false);
      notifyKeyCapture(answer, notify, verify);
    },
    onSettled: () => query.refetch(),
  });

  // Re-verify an org key against its provider. A successful verify writes NO
  // onboarding state (#4613: `UPSTREAM_LADDER` takes `key_set` on the next
  // settle); a 422 persists the row as `invalid`, so we always refetch and
  // let the card reflect server truth. The toast is what tells you the click DID
  // something (#924), the refusal in the server's own words (plan 101 slice 17
  // row 17-15); keyed per row, so a double click sends one.
  const verifying = createKeyedMutation({
    fallback: t('account_keys.verify_failed_toast'),
    onError: (message) => notify(message, 'error'),
  });
  const verify = (row: KeyRow): Promise<void> =>
    verifying.run(row.id, async () => {
      try {
        const notice = keyVerifyNotice(await verifyKey(row.id, token));
        notify(notice.message, notice.tone);
      } finally {
        await query.refetch();
      }
    });

  const revoke = createMutation({
    call: (id: string) => revokeKey(id, token),
    fallback: t('account_keys.revoke_error'),
    onSuccess: async () => {
      setRevoking(null);
      await query.refetch();
      // The dialog restored focus to the card's revoke button, but the refetch
      // just removed that card — don't leave keyboard focus on <body> (or on
      // a detached node, where engines differ).
      queueMicrotask(() => {
        const active = document.activeElement;
        if (!active || active === document.body || !active.isConnected) heading?.focus();
      });
    },
    onError: (message) => notify(message, 'error'),
  });

  // One busy state for both writes, as when they shared a signal.
  const busy = (): boolean => add.pending() || revoke.pending();
  const addError = add.error;

  const confirmRevoke = (): void => {
    const row = revoking();
    if (!row) return;
    void revoke.run(row.id);
  };

  // BYO log sink (#448) — set/clear the customer-owned Postgres raw logs mirror
  // to. Its whole ladder (the 400/422 copy split included) is `lib/log-sink-editor.ts`.
  const sink = createLogSinkEditor(token);

  return (
    <AccountKeysView
      // No token = prod pre-SSO: gate the whole route so add/revoke controls
      // (which could only fire unauthenticated) never render.
      hasSession={token !== null}
      personalKeysOnly={personalKeysOnly()}
      headingRef={(el) => {
        heading = el;
      }}
      keysQuery={query}
      addOpen={addOpen()}
      addBusy={busy()}
      addError={addError()}
      onOpenAdd={() => {
        add.clearError();
        setAddOpen(true);
      }}
      onSubmitAdd={(input: AddKeyInput) => void add.run(input)}
      onCloseAdd={() => setAddOpen(false)}
      onProbeModels={(input: ProbeModelsInput) => probeProviderModels(input, token)}
      onVerify={(row) => void verify(row)}
      onAskRevoke={setRevoking}
      revoking={revoking()}
      onCloseRevoke={() => setRevoking(null)}
      onConfirmRevoke={confirmRevoke}
      onAskRotate={rotate.open}
      rotating={rotate.rotating()}
      rotateBusy={rotate.busy()}
      rotateError={rotate.error()}
      onSubmitRotate={rotate.submit}
      onCloseRotate={rotate.close}
      onSetPriority={canRank() ? (row, value) => void priority.save(row, value) : undefined}
      priorityBusy={priority.isBusy}
      capacity={capacity}
      sinkStatus={sink.status.data}
      sinkBusy={sink.busy()}
      sinkError={sink.error()}
      onSaveSink={sink.save}
      onClearSink={sink.clear}
      toast={toast()}
      onToastDismiss={dismiss}
    />
  );
}
